/*
 * ******************************************************
 * Copyright (c) 2022 Jenke & Rauschert GbR <info@aiandor.de> - All Rights Reserved
 *
 * This file is part of a software product owned by the Jenke & Rauschert GbR.
 * Unauthorized distribution of this file is strictly prohibited.
 * ******************************************************
 */

import {useAppDispatch, useAppSelector} from "../store";
import {Button, DatePicker, Drawer, Dropdown, Form, Input, InputNumber, Menu, Modal, Result, Space, Spin} from "antd";
import React, {useEffect, useState} from "react";
import {closeDrawer} from "../reducers/drawers-reducer";
import {triggerReload} from "../reducers/reloadsignal-reducer";
import {del, get, patch, post} from "../core/request";
import {IconSet} from "../components/icons";
import {CustomButton} from "../components/buttons";
import {CTEvent, MGEvent, NMEvent, ORMObject, PETEvent, RFEvent, XREvent} from "../core/datamodel";
import {ViewState} from "../states";
import {ToolTwoTone} from "@ant-design/icons";

const stack_selector = (state: any) => state.drawer.event
const key = "event"

interface DrawerProps {
    size?: 'default' | 'large'
}


export function EventDrawer(props: DrawerProps) {
    const stack = useAppSelector(stack_selector)
    const drawer_stack = useAppSelector(state => state.drawer.stack)
    const dispatch = useAppDispatch();
    const [object, setObject] = useState({} as ORMObject);
    const [form] = Form.useForm();
    const [state, setState] = useState(ViewState.loading);

    const [deviceType, setDeviceType] = useState("CT");
    const [uri, setUri] = useState("");
    const [title, setTitle] = useState("");

    //@ts-ignore
    const isNew = () => !(!!object?.props?.uuid)


    useEffect(() => {
        if (!!stack[stack.length - 1]) {
            setDeviceType(stack[stack.length - 1][0])

            let response_name: string

            switch (deviceType) {
                case "MG":
                    setUri("mg_events")
                    response_name = "mg_event"
                    setTitle("MG Event")
                    break
                case "NM":
                    setUri("nm_events")
                    response_name = "nm_event"
                    setTitle("NM Event")
                    break
                case "PET":
                    setUri("pet_events")
                    response_name = "pet_event"
                    setTitle("PET Event")
                    break
                case "RF":
                    setUri("rf_events")
                    response_name = "rf_event"
                    setTitle("RF Event")
                    break
                case "XR":
                    setUri("xr_events")
                    response_name = "xr_event"
                    setTitle("XR Event")
                    break
                case "CT":
                default:
                    setUri("ct_events")
                    response_name = "ct_event"
                    setTitle("CT Event")
                    break
            }


            if (stack[stack.length - 1][1] !== undefined) {
                setState(ViewState.loading);
                get(response_name + "s/" + stack[stack.length - 1][1]).done((response) => {
                    createObject(response.data[response_name]);
                }).fail(() => setState(ViewState.failed))
            } else {
                createObject({exam: {uuid: stack[stack.length - 1][2]}});
            }
        }
    }, [stack, form])

    const createObject = (objectProps: any) => {
        let event
        switch (deviceType) {
            case "CT":
            default:
                event = new CTEvent(objectProps);
                break
            case "NM":
                event = new NMEvent(objectProps);
                break
            case "MG":
                event = new MGEvent(objectProps);
                break
            case "PET":
                event = new PETEvent(objectProps);
                break
            case "RF":
                event = new RFEvent(objectProps);
                break
            case "XR":
                event = new XREvent(objectProps);
                break
        }

        form.resetFields()
        form.setFieldsValue({
            ...event?.props,
        })
        console.debug("Event", event, objectProps)
        setObject(event)
        setState(ViewState.success)
    }

    const onFormFinish = () => {
        let values = form.getFieldsValue()

        object.update({
            ...values,
        })

        let req: JQueryPromise<any>;
        if (isNew()) {
            req = post(uri, object.dump())
        } else {
            req = patch(uri + "/" + object.props.uuid, object.dump())
        }

        req.then(() => {
                dispatch(triggerReload(key))
                dispatch(closeDrawer(key))
            },
            (e) => {
                console.error(e);
            })
    }

    const onDelete = () => {
        Modal.confirm({
            title: "Eintrag wirklich gelöscht werden?",
            content: "Diese Aktion kann nicht rückgängig gemacht werden!",
            maskClosable: true,
            cancelText: 'Abbrechen',
            autoFocusButton: 'cancel',
            okText: 'Löschen',
            okType: 'danger',
            onOk: () => {
                //@ts-ignore
                del(uri + "/" + object.props.uuid)
                    .then(
                        () => {
                            dispatch(triggerReload(key))
                            dispatch(closeDrawer(key))
                        },
                        (e) => {
                            console.error(e);
                        })
                return false;
            }
        })
    }

    const form_layout = {
        labelCol: {span: 6},
        wrapperCol: {span: 16},
    };

    const form_content = () => {
        switch (deviceType) {
            case "CT":
                return (<>
                        <Form.Item label="pitch_fact" name="pitch_fact">
                            <InputNumber name="pitch_fact" placeholder="pitch_fact"/>
                        </Form.Item>

                        <Form.Item label="ssde" name="ssde">
                            <InputNumber name="ssde" placeholder="ssde"/>
                        </Form.Item>

                        <Form.Item label="scan_length" name="scan_length">
                            <InputNumber name="scan_length" placeholder="scan_length"/>
                        </Form.Item>

                        <Form.Item label="ctdi" name="ctdi">
                            <InputNumber name="ctdi" placeholder="ctdi"/>
                        </Form.Item>

                        <Form.Item label="ctdi_refInt" name="ctdi_refInt">
                            <InputNumber name="ctdi_refInt" placeholder="ctdi_refInt"/>
                        </Form.Item>

                        <Form.Item label="ctdi_refNat" name="ctdi_refNat">
                            <InputNumber name="ctdi_refNat" placeholder="ctdi_refNat"/>
                        </Form.Item>

                        <Form.Item label="ctdi_wRefInt" name="ctdi_wRefInt">
                            <InputNumber name="ctdi_wRefInt" placeholder="ctdi_wRefInt"/>
                        </Form.Item>

                        <Form.Item label="ctdi_wRefNat" name="ctdi_wRefNat">
                            <InputNumber name="ctdi_wRefNat" placeholder="ctdi_wRefNat"/>
                        </Form.Item>

                        <Form.Item label="dlp" name="dlp">
                            <InputNumber name="dlp" placeholder="dlp"/>
                        </Form.Item>

                        <Form.Item label="dlp_refInt" name="dlp_refInt">
                            <InputNumber name="dlp_refInt" placeholder="dlp_refInt"/>
                        </Form.Item>

                        <Form.Item label="dlp_refNat" name="dlp_refNat">
                            <InputNumber name="dlp_refNat" placeholder="dlp_refNat"/>
                        </Form.Item>

                        <Form.Item label="dlp_wRefInt" name="dlp_wRefInt">
                            <InputNumber name="dlp_wRefInt" placeholder="dlp_wRefInt"/>
                        </Form.Item>

                        <Form.Item label="dlp_wRefNat" name="dlp_wRefNat">
                            <InputNumber name="dlp_wRefNat" placeholder="dlp_wRefNat"/>
                        </Form.Item>
                    </>
                )
            default:
                return <></>
        }
    }

    const form_element = (
        <Form
            form={form}
            onFinish={onFormFinish}
            {...form_layout}
            requiredMark={true}
        >
            <Form.Item label="acq_protocol" name="acq_protocol">
                <Input name="acq_protocol" placeholder="acq_protocol"/>
            </Form.Item>

            <Form.Item label="acq_type" name="acq_type">
                <Input name="acq_type" placeholder="acq_type"/>
            </Form.Item>

            <Form.Item label="acq_time" name="acq_time">
                <DatePicker name="acq_time" showTime allowClear/>
            </Form.Item>

            <Form.Item label="radlex_body_region" name="radlex_body_region">
                <Input name="radlex_body_region" placeholder="radlex_body_region"/>
            </Form.Item>

            <Form.Item label="protocol" name="protocol">
                <Input name="protocol" placeholder="protocol"/>
            </Form.Item>

            <Form.Item label="anat_region" name="anat_region">
                <Input name="anat_region" placeholder="anat_region"/>
            </Form.Item>

            <Form.Item label="rpid" name="rpid">
                <InputNumber name="rpid" placeholder="rpid"/>
            </Form.Item>

            <Form.Item label="phantom" name="phantom">
                <InputNumber name="phantom" placeholder="phantom"/>
            </Form.Item>

            <Form.Item label="pat_pos" name="pat_pos">
                <Input name="pat_pos" placeholder="pat_pos"/>
            </Form.Item>

            {form_content()}

            {/* Buttons */}
            <Form.Item
                label=" "
                colon={false}
            >
                <Form.Item
                    style={{display: 'inline-block'}}
                >
                    <Button type="primary" htmlType="submit">
                        <IconSet type='save'/>&nbsp;Speichern
                    </Button>
                </Form.Item>
                <Form.Item
                    style={{display: 'inline-block', margin: '0 24px'}}
                >
                    <Button onClick={() => dispatch(closeDrawer(key))}>
                        <IconSet type='cancel'/>&nbsp;Änderungen verwerfen
                    </Button>
                </Form.Item>
            </Form.Item>
        </Form>
    )

    const menu = (
        isNew() ? (<></>) : (
            <Dropdown
                arrow
                overlay={
                    <Menu style={{padding: 12}} items={[
                        {
                            key: "delete",
                            danger: true,
                            onClick: onDelete,
                            label: <><IconSet type='delete'/> Löschen</>
                        },
                    ]}
                    />
                }
                placement="bottomRight"
                className='button-edit'
            >
                <CustomButton mode='more'/>
            </Dropdown>)
    )

    const stateSwitch = () => {
        switch (state) {
            case ViewState.initial:
            case ViewState.loading:
                return <Spin spinning>
                    {form_element}
                </Spin>

            case ViewState.failed:
                return <>
                    <Result
                        status="404"
                        title="Fehler"
                        subTitle="Entschuldigung, das Event konnten nicht geladen werden!"
                    />
                    <div style={{display: "none"}}>
                        {form_element}
                    </div>
                </>

            case ViewState.success:
                return form_element
        }
    }

    const construction = (
        <>
            <Result
                icon={<ToolTwoTone/>}
                title='An dieser Seite wird derzeit noch gearbeitet.'
            />
            <div style={{display: "none"}}>
                {form_element}
            </div>
        </>
    )

    return (
        <Drawer
            open={stack.length > 0 && drawer_stack[drawer_stack.length - 1] === key}
            size={props.size}
            onClose={() => dispatch(closeDrawer(key))}
            forceRender={true}
            title={
                <Space direction="horizontal" style={{width: '100%', justifyContent: 'center'}}>
                    <h1 style={{marginBottom: 0}}>{title + (isNew() ? " hinzufügen" : " bearbeiten")}</h1>
                </Space>
            }
            extra={menu}
            zIndex={drawer_stack[drawer_stack.length - 1] === key ? 1000 : 900}
        >
            {construction/* TODO stateSwitch()*/}
        </Drawer>
    )
}


EventDrawer.defaultProps = {
    size: 'large'
}